import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, BedDouble, Bath, Ruler, MapPin, Loader2, BadgeCheck, Send, Navigation, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import Reveal from '../../components/public/Reveal';
import CnibMap from '../../components/CnibMap';
import ShareMenu from '../../components/ShareMenu';
import api from '../../lib/api';
import { OPERATION_LABELS, PROPERTY_TYPE_LABELS, PROPERTY_STATUS_LABELS } from '../../lib/constants';

const OP_BADGE = { venta: 'bg-orange-500', alquiler: 'bg-cnib-blue', anticretico: 'bg-emerald-500' };

export default function PropiedadDetalle() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [contact, setContact] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api
      .get(`/properties/${id}`)
      .then(({ data }) => setProperty(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cnib-navy">
        <Loader2 className="h-10 w-10 animate-spin text-orange-500" />
      </div>
    );
  }

  if (notFound || !property) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-cnib-navy px-4 text-center">
        <h1 className="font-heading text-3xl font-bold text-white">Propiedad no disponible</h1>
        <Link to="/propiedades" className="flex items-center gap-2 rounded-full bg-orange-500 px-6 py-3 font-bold text-white">
          <ArrowLeft className="h-4 w-4" /> Ver propiedades
        </Link>
      </div>
    );
  }

  const owner = property.owner || {};
  const whatsappDigits = (owner.whatsapp || '').replace(/[^0-9]/g, '');
  const whatsappUrl = whatsappDigits
    ? `https://wa.me/${whatsappDigits}?text=${encodeURIComponent(`Hola ${owner.name}, me interesa la propiedad "${property.title}" publicada en CNIB Connect.`)}`
    : null;

  const handleContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/contact', {
        name: contact.name,
        email: contact.email,
        subject: `Consulta propiedad: ${property.title}`,
        message: `${contact.message}\n\nPropiedad: ${window.location.href}`,
      });
      toast.success('Consulta enviada. El equipo CNIB la derivará al socio.');
      setContact({ name: '', email: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar la consulta.');
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    'w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15';

  return (
    <div className="pt-20 md:pt-24">
      <div className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <Link to="/propiedades" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition-colors hover:text-cnib-blue" data-testid="property-back-link">
          <ArrowLeft className="h-4 w-4" /> Volver a propiedades
        </Link>

        <div className="mt-5 grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Reveal>
              <div className="relative overflow-hidden rounded-3xl bg-slate-100">
                {property.images?.length > 0 ? (
                  <img src={property.images[activeImg]} alt={property.title} className="h-[300px] w-full object-cover md:h-[440px]" data-testid="property-image" />
                ) : (
                  <div className="flex h-[300px] items-center justify-center text-slate-400 md:h-[440px]">Sin fotos</div>
                )}
                <span className={`absolute left-5 top-5 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-white ${OP_BADGE[property.operation]}`}>
                  {OPERATION_LABELS[property.operation]}
                </span>
                <span className="absolute right-5 top-5" data-testid="property-share-button-anchor">
                  <ShareMenu
                    title={`${property.title} — CNIB Connect`}
                    url={window.location.pathname}
                    label=""
                    compact
                    testid="property-share"
                  />
                </span>
              </div>
              {property.images?.length > 1 && (
                <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
                  {property.images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(i)}
                      className={`h-20 w-28 shrink-0 overflow-hidden rounded-xl border-2 transition-all ${i === activeImg ? 'border-orange-500' : 'border-transparent opacity-70 hover:opacity-100'}`}
                      data-testid={`property-thumb-${i}`}
                    >
                      <img src={img} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-8">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-full bg-cnib-blue/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-cnib-blue">
                    {PROPERTY_TYPE_LABELS[property.property_type]}
                  </span>
                  <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${property.status === 'available' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-slate-200 text-slate-500'}`}>
                    {PROPERTY_STATUS_LABELS[property.status]}
                  </span>
                </div>
                <h1 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl" data-testid="property-title">
                  {property.title}
                </h1>
                <p className="mt-2 flex items-center gap-1.5 text-slate-500">
                  <MapPin className="h-4 w-4 text-orange-500" />
                  {property.address ? `${property.address} · ` : ''}{property.zone ? `${property.zone}, ` : ''}{property.city}
                </p>

                <div className="mt-6 grid grid-cols-3 gap-4 sm:max-w-md">
                  {property.bedrooms > 0 && (
                    <div className="rounded-2xl bg-white p-4 text-center shadow-sm">
                      <BedDouble className="mx-auto h-5 w-5 text-cnib-blue" />
                      <p className="mt-1 font-heading text-lg font-bold text-slate-900">{property.bedrooms}</p>
                      <p className="text-xs text-slate-500">Dormitorios</p>
                    </div>
                  )}
                  {property.bathrooms > 0 && (
                    <div className="rounded-2xl bg-white p-4 text-center shadow-sm">
                      <Bath className="mx-auto h-5 w-5 text-cnib-blue" />
                      <p className="mt-1 font-heading text-lg font-bold text-slate-900">{property.bathrooms}</p>
                      <p className="text-xs text-slate-500">Baños</p>
                    </div>
                  )}
                  {property.area_m2 > 0 && (
                    <div className="rounded-2xl bg-white p-4 text-center shadow-sm">
                      <Ruler className="mx-auto h-5 w-5 text-cnib-blue" />
                      <p className="mt-1 font-heading text-lg font-bold text-slate-900">{property.area_m2}</p>
                      <p className="text-xs text-slate-500">m²</p>
                    </div>
                  )}
                </div>

                {property.description && (
                  <div className="mt-8">
                    <h2 className="font-heading text-xl font-bold text-slate-900">Descripción</h2>
                    {property.description.split('\n').filter(Boolean).map((p, i) => (
                      <p key={i} className="mt-3 leading-relaxed text-slate-600">{p}</p>
                    ))}
                  </div>
                )}

                {property.lat && property.lng && (
                  <div className="mt-8">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <h2 className="font-heading text-xl font-bold text-slate-900">Ubicación</h2>
                      <div className="flex flex-wrap gap-2">
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${property.lat},${property.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="property-directions"
                          className="inline-flex items-center gap-1.5 rounded-full bg-cnib-blue px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-cnib-blue/90"
                        >
                          <Navigation className="h-3.5 w-3.5" />
                          Cómo llegar
                        </a>
                        <a
                          href={`https://wa.me/?text=${encodeURIComponent(
                            `Ubicación de la propiedad "${property.title}" en CNIB:\nhttps://www.google.com/maps/search/?api=1&query=${property.lat},${property.lng}\n\nFicha completa: ${typeof window !== 'undefined' ? window.location.href : ''}`
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="property-share-location-whatsapp"
                          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-emerald-600"
                        >
                          <MessageCircle className="h-3.5 w-3.5" />
                          Compartir por WhatsApp
                        </a>
                      </div>
                    </div>
                    <CnibMap
                      center={[property.lng, property.lat]}
                      zoom={14}
                      markers={[{ id: property.property_id, lat: property.lat, lng: property.lng, color: '#F97316', icon: 'home', size: 'lg' }]}
                      className="mt-4 h-72 w-full overflow-hidden rounded-2xl"
                    />
                  </div>
                )}
              </div>
            </Reveal>
          </div>

          <div className="space-y-6">
            <Reveal delay={0.15}>
              <div className="rounded-3xl border border-slate-100 bg-white p-7 shadow-lg">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Precio</p>
                <p className="mt-1 font-heading text-3xl font-extrabold text-cnib-blue" data-testid="property-price">
                  US$ {Number(property.price_usd).toLocaleString('es-BO')}
                  {property.operation === 'alquiler' && <span className="text-sm font-semibold text-slate-400"> /mes</span>}
                </p>

                <div className="mt-6 flex items-center gap-3 rounded-2xl bg-slate-50 p-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-cnib-navy font-heading text-lg font-bold text-orange-500">
                    {owner.picture ? <img src={owner.picture} alt="" className="h-full w-full rounded-full object-cover" /> : owner.name?.charAt(0)}
                  </span>
                  <div className="min-w-0">
                    <p className="flex items-center gap-1 truncate text-sm font-bold text-slate-900">
                      {owner.name}
                      <BadgeCheck className="h-4 w-4 shrink-0 text-emerald-500" />
                    </p>
                    <p className="truncate text-xs text-slate-500">
                      Socio CNIB {owner.member_number} {owner.company ? `· ${owner.company}` : ''}
                    </p>
                  </div>
                </div>

                {whatsappUrl && (
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-agent-whatsapp"
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3.5 font-bold text-white transition-colors hover:bg-emerald-600"
                  >
                    <Send className="h-4 w-4" /> WhatsApp del socio
                  </a>
                )}
                {owner.credential_token && (
                  <Link
                    to={`/p/${owner.credential_token}`}
                    data-testid="agent-public-card-link"
                    className="mt-3 block w-full rounded-xl border-2 border-cnib-blue/20 py-3 text-center text-sm font-bold text-cnib-blue transition-colors hover:bg-cnib-blue hover:text-white"
                  >
                    Ver credencial del socio
                  </Link>
                )}
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <form onSubmit={handleContact} className="space-y-4 rounded-3xl border border-slate-100 bg-white p-7 shadow-sm" data-testid="property-contact-form">
                <h3 className="font-heading text-lg font-bold text-slate-900">Consulta por esta propiedad</h3>
                <input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} required minLength={2} placeholder="Tu nombre" className={inputCls} data-testid="property-contact-name" />
                <input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} required placeholder="Tu correo" className={inputCls} data-testid="property-contact-email" />
                <textarea value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} required minLength={5} rows={3} placeholder="Hola, me interesa esta propiedad…" className={inputCls} data-testid="property-contact-message" />
                <button type="submit" disabled={sending} className="w-full rounded-xl bg-orange-500 py-3.5 font-bold text-white transition-colors hover:bg-orange-600 disabled:opacity-60" data-testid="property-contact-submit">
                  {sending ? 'Enviando…' : 'Enviar consulta'}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}
